<template>
  <div class="dashboard-womp">
    <loading-placeholder v-if="isLoading" :lines="5" />
    <loading-placeholder v-if="isLoading" :lines="5" />
    <template v-else>
      <div class="womp-profile">
        <div class="womp-profile__header marker--dashboard-womp--profile">
          <a :href="RouteAssistant.getProfileUrl($route.params.accountId)">
            <avatar :size="32" :person="$permissions.loggedInUser" />
          </a>
          <span class="scripty">What's on my plate?</span>
        </div>
        <div class="stats">
          <div class="stats-category">
            <h5>Assigned</h5>
            <div v-if="assignedTaskCount > 0 || assignedConversationCount > 0">
              <div v-if="assignedTaskCount > 0" class="task">
                <router-link
                  :to="{ name: routerNames.ALL_TASKS, query: { 
                    status_type: StatusTypeValues.ACTIVE,
                    assignee_ids: `${$permissions.loggedInUser.id}`,
                  } }"
                  :title="`${limitedCountText(assignedTaskCount)} assigned task(s)`"
                >
                  <i class="ddi ddi-task" />
                  <span>{{limitedCountText(assignedTaskCount)}}</span>
                </router-link>
              </div>
              <div v-if="assignedConversationCount > 0" class="conversation">
                <router-link
                  :to="{ name: routerNames.ALL_CONVERSATIONS, query: { 
                    status_type: StatusTypeValues.ACTIVE,
                    assignee_ids: `${$permissions.loggedInUser.id}`,
                  } }"
                  :title="`${limitedCountText(assignedConversationCount)} assigned conversation(s)`"
                >
                  <i class="ddi ddi-conversation" />
                  <span>{{limitedCountText(assignedConversationCount)}}</span>
                </router-link>
              </div>
            </div>
            <div v-else>
              <div class="na">N/A</div>
            </div>
          </div>
          <div class="stats-category">
            <h5>Watching</h5>
            <div v-if="watchedTaskCount > 0 || watchedConversationCount > 0">
              <div v-if="watchedTaskCount > 0" class="task">
                <router-link
                  :to="{ name: routerNames.ALL_TASKS, query: { 
                    status_type: StatusTypeValues.ACTIVE,
                    only_watching: 'true',
                  } }"
                  :title="`${limitedCountText(watchedTaskCount)} watched task(s)`"
                >
                  <i class="ddi ddi-task" />
                  <span>{{limitedCountText(watchedTaskCount)}}</span>
                </router-link>
              </div>
              <div v-if="watchedConversationCount > 0" class="conversation">
                <router-link
                  :to="{ name: routerNames.ALL_CONVERSATIONS, query: { 
                    status_type: StatusTypeValues.ACTIVE,
                    only_watching: 'true',
                  } }"
                  :title="`${limitedCountText(watchedConversationCount)} assigned conversation(s)`"
                >
                  <i class="ddi ddi-conversation" />
                  <span>{{limitedCountText(watchedConversationCount)}}</span>
                </router-link>
              </div>
            </div>
            <div v-else>
              <div class="na">N/A</div>
            </div>
          </div>
          <div class="stats-category">
            <h5>Created</h5>
            <div v-if="createdTaskCount > 0">
              <div class="task">
                <router-link
                  :to="{ name: routerNames.ALL_TASKS, query: { 
                    status_type: StatusTypeValues.ACTIVE,
                    creator_ids: `${$permissions.loggedInUser.id}`,
                  } }"
                  :title="`${limitedCountText(createdTaskCount)} created task(s)`"
                >
                  <i class="ddi ddi-task" />
                  <span>{{limitedCountText(createdTaskCount)}}</span>
                </router-link>
              </div>
            </div>
            <div v-else>
              <div class="na">N/A</div>
            </div>
          </div>
        </div>
      </div>
      <div class="due-log">
        <div v-for="(dueRange, index) in dueLog.listDueRangeInfos" :key="index" class="due-day">
          <div :class="[{'overdue': dueRange.isOverdueRange}, {'today': dueRange.isToday}]" class="due-day__header">
            <i v-if="dueRange.isOverdueRange" class="ddi ddi-warning" /> {{ dueRange.title }}
          </div>
          <div :class="{ 'borderless': index === dueLog.listDueRangeInfos.length - 1 }" class="due-day__items">
            <div v-if="isNoDueItemsDay(dueRange)" class="nothing-due">
              Nothing due today
            </div>
            <div v-if="isMultiConvoDayRange(dueRange)" class="item convo">
              <router-link
                :to="{ name: routerNames.ALL_CONVERSATIONS, query: getRouteParamsForDueRange(dueRange)}"
                :title="`${limitedCountText(dueRange.dueConversationCount)} assigned conversations`"
              >
                <div class="multi-info">
                  <i class="ddi ddi-conversation" /> {{dueRange.dueConversationCount}} conversations
                </div>
              </router-link>
            </div>
            <div v-if="isMultiTaskDayRange(dueRange)" class="item">
              <router-link
                :to="{ name: routerNames.ALL_TASKS, query: getRouteParamsForDueRange(dueRange)}"
                :title="`${limitedCountText(dueRange.dueTaskCount)} assigned tasks`"
              >
                <div class="multi-info">
                  <i class="ddi ddi-task" />  {{dueRange.dueTaskCount}} tasks
                </div>
              </router-link>
            </div>
            <div v-if="isSingleConvoDay(dueRange)" class="item convo">
              <router-link :to="getItemRoute(dueRange.singleDueConvo)">
                <div class="title">
                  <strong>#{{dueRange.singleDueConvo.refNumber}}:</strong> {{dueRange.singleDueConvo.title}}
                </div>
                <div class="entity">
                  {{dueRange.singleDueConvo.entity.name}}
                </div>
                <div class="meta">
                  <priority-chicklet :priority="dueRange.singleDueConvo.priority" />
                  <status-chicklet :status="dueRange.singleDueConvo.status" />
                </div>
              </router-link> 
            </div>
            <div v-if="isSingleTaskDay(dueRange)" class="item">
              <router-link :to="getItemRoute(dueRange.singleDueTask)">
                <div class="title">
                  <strong>#{{dueRange.singleDueTask.refNumber}}:</strong> {{dueRange.singleDueTask.title}}
                </div>
                <div class="entity">
                  {{dueRange.singleDueTask.entity.name}}
                </div>
                <div class="meta">
                  <priority-chicklet :priority="dueRange.singleDueTask.priority" />
                  <status-chicklet :status="dueRange.singleDueTask.status" />
                </div>
              </router-link> 
            </div>
          </div>
        </div>
      </div>  
    </template>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import dayjs from '@/helpers/dayjs'
import * as routerNames from '@/services/router/router.names'
import {
  ContentPlaceholders,
  ContentPlaceholdersText,
} from '@/components/global/content-placeholders'

/* Mixins */

/* Components */

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { DueTypeValues, StatusTypeValues } from '@/types'

/* API */
import DashboardAPI from '@/api/modules/dashboard.api'
import {
  IDashboardOutputDashboardItem,
  IDashboardOutputWompDueLog,
  IDashboardOutputWompDueLogWOMPDueRangeInfo,
} from '@/api/types/response.types'

export default defineComponent({
  name: 'DashboardWomp',

  components: {
    ContentPlaceholders,
    ContentPlaceholdersText,
  },

  data() {
    return {
      DueTypeValues,
      StatusTypeValues,
      pinnedItems: [] as IDashboardOutputDashboardItem[],
      routerNames,
      isLoading: true,
      navPanels: [] as any[],

      assignedTaskCount: 0,
      assignedConversationCount: 0,
      watchedTaskCount: 0,
      watchedConversationCount: 0,
      createdTaskCount: 0,

      dueLog: {} as IDashboardOutputWompDueLog,

      RouteAssistant,
    }
  },

  async created() {
    try {
      this.isLoading = true
      await this.getWompStats()
      this.isLoading = false
    } catch (error) {
      this.$error.emit(error)
    }
  },

  methods: {
    isNoDueItemsDay(dueRangeInfo: IDashboardOutputWompDueLogWOMPDueRangeInfo) {
      return (
        !this.isMultiConvoDayRange(dueRangeInfo) &&
        !this.isSingleConvoDay(dueRangeInfo) &&
        !this.isMultiTaskDayRange(dueRangeInfo) &&
        !this.isSingleTaskDay(dueRangeInfo)
      )
    },
    isMultiConvoDayRange(
      dueRangeInfo: IDashboardOutputWompDueLogWOMPDueRangeInfo,
    ) {
      return dueRangeInfo.dueConversationCount > 1
    },
    isSingleConvoDay(dueRangeInfo: IDashboardOutputWompDueLogWOMPDueRangeInfo) {
      return dueRangeInfo.singleDueConvo !== null
    },
    isMultiTaskDayRange(
      dueRangeInfo: IDashboardOutputWompDueLogWOMPDueRangeInfo,
    ) {
      return dueRangeInfo.dueTaskCount > 1
    },
    isSingleTaskDay(dueRangeInfo: IDashboardOutputWompDueLogWOMPDueRangeInfo) {
      return dueRangeInfo.singleDueTask !== null
    },
    async getWompStats() {
      try {
        const response = await DashboardAPI.get_WompStats()
        this.assignedTaskCount = response.countInfo.assignedTaskCount
        this.assignedConversationCount =
          response.countInfo.assignedConversationCount
        this.watchedTaskCount = response.countInfo.watchedTaskCount
        this.watchedConversationCount =
          response.countInfo.watchedConversationCount
        this.createdTaskCount = response.countInfo.createdTaskCount
        this.dueLog = response.dueLog
      } catch (error) {
        this.$error.emit(error)
      }
    },
    limitedCountText(count: number): string {
      return count < 100 ? `${count}` : '99+'
    },
    getRouteParamsForDueRange(
      dueRange: IDashboardOutputWompDueLogWOMPDueRangeInfo,
    ): any {
      return {
        status_type: StatusTypeValues.ACTIVE,
        due_type: DueTypeValues.DATE_RANGE,
        start_due_date: dayjs(dueRange.startDay).format('YYYY-MM-DD'),
        end_due_date: dayjs(dueRange.endDay).format('YYYY-MM-DD'),
        assignee_ids: `${this.$permissions.loggedInUser.id}`,
      }
    },
    getItemRoute(item: IDashboardOutputDashboardItem) {
      if (item.isConversation) {
        return RouteAssistant.getConversationRoute(
          this.$route.params.accountId,
          item.entity.id,
          item.id,
          item.entity.name,
          item.refNumber,
          item.title,
        )
      } else {
        return RouteAssistant.getTaskRoute(
          this.$route.params.accountId,
          item.entity.id,
          item.id,
          this.$permissions,
          item.entity.name,
          item.refNumber,
          item.title,
        )
      }
    },
  },
})
</script>

<style lang="scss">
.dashboard-womp {
  height: 100%;
  border-right: $border--dotted-light;
  padding: 0 30px 16px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  @include flex-column-vertical-spacing(40px);

  @include media-breakpoint-down(md) {
    border-right: none;
    padding: 0;
  }

  .womp-profile {
    width: 100%;
    margin-top: 10px;

    &__header {
      display: flex;
      align-items: center;
      margin-bottom: 18px;
      @include flex-row-horizontal-spacing(10px);

      > span {
        font-size: 26px;
        color: var(--color-text-primary);
      }

      @include media-breakpoint-down(md) {
        margin-top: 0;
        justify-content: center;
      }
    }

    > .stats {
      display: flex;
      @include flex-row-horizontal-spacing(1px);

      .stats-category {
        width: 100%;
        align-self: flex-start;
        display: flex;
        flex-direction: column;
        align-items: center;
        border: $border--solid-light;
        border-radius: 4px;
        background-color: var(--color-background-secondary);

        &.important {
          border-color: $color--red;
        }

        > h5 {
          margin: 4px 0;
          text-align: center;
          color: var(--color-text-primary);
        }

        > div {
          display: flex;
          justify-content: center;
          width: 100%;
          @include flex-row-horizontal-spacing(1px);

          > div {
            border-radius: 4px;
            background-color: var(--color-background-header);
            border-top: var(--border-solid-extra-light);
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            @include lettering--p-sm;

            &.task {
              &:hover:not(.na) {
                background-color: var(--color-background-blue-15);
              }
              a {
                color: $projects--theme-color;
              }
            }
            &.conversation {
              &:hover:not(.na) {
                background-color: $color--green-15;
              }
              a {
                color: $mailboxes--theme-color;
              }
            }

            &.na {
              color: $color--gray-45;
            }

            a {
              display: flex;
              align-items: center;
              font-weight: $bold;
              letter-spacing: -0.02em;

              &:hover {
                text-decoration: none;
              }

              i {
                font-size: 14px;
              }

              .ddi {
                padding-right: 2px;
              }
            }
          }
        }
      }
    }
  }

  > .due-log {
    width: 100%;
    padding-left: 12px;
    position: relative;

    .due-day {
      &__header {
        margin-left: -10px;
        border-radius: 10px;
        background-color: var(--color-background-secondary);
        border: $border--solid-light;
        padding: 1px 8px;
        width: fit-content;
        @include lettering--p-xs;
        color: var(--color-text-secondary);

        &.overdue {
          color: $color--white;
          background-color: $color--red;
        }

        &.today {
          background-color: $color--gray-75;
          color: $color--white;
        }
      }

      &__items {
        margin: 0;
        border-left: $border--dotted-dark;
        padding: 10px 0 20px 10px;

        &.borderless {
          padding-bottom: 10px;
        }

        .nothing-due {
          display: block;
          border: dashed 1px $color--gray-30;
          border-left: solid 3px $color--gray-45;
          border-radius: 4px;
          padding: 8px;
          margin-bottom: 4px;
          @include lettering--p-sm;
          color: var(--color-text-primary);
        }

        .item {
          display: block;
          border: dotted 1px $color--blue-60;
          border-left: solid 3px $projects--theme-color;
          border-radius: 4px;
          padding: 8px;
          margin-bottom: 4px;
          background-color: var(--color-background-task);

          a:hover {
            text-decoration: none;
          }

          .multi-info {
            @include lettering--p-sm;
            color: $projects--theme-color;
            font-weight: $bold;
          }

          &.convo {
            border: dotted 1px $color--green-60;
            border-left: solid 3px $mailboxes--theme-color;

            .multi-info {
              color: $mailboxes--theme-color;
            }
          }

          &:hover {
            @include standard-box-shadow;
          }

          .title {
            @include lettering--p-sm;
            margin-bottom: 4px;
            @include multi-line-ellipsis(2);
          }

          .entity {
            @include lettering--p-sm--no-color;
            @include single-line-ellipsis;
            color: var(--color-text-third);
            margin-bottom: 10px;
          }

          .meta {
            display: flex;
            flex-wrap: wrap;
            @include flex-row-horizontal-spacing(4px);
            @include flex-row-vertical-spacing-on-wrap(4px);

            > * {
              @include single-line-ellipsis;
            }
          }

          &:hover {
            text-decoration: none;
          }

          @include lettering--p-sm;
        }
      }
    }
  }
}
</style>
